import { WindowTitle } from "@saleor/components/WindowTitle";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { CoverageTypeEnum } from "@saleor/types/globalTypes";
import createMetadataCreateHandler from "@saleor/utils/handlers/metadataCreateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import React from "react";
import { useIntl } from "react-intl";

import CollectionCreatePage, {
  CollectionCreatePageFormData
} from "../components/CollectionCreatePage/CollectionCreatePage";
import { useCollectionCreateMutation } from "../mutations";
import { collectionListUrl, collectionUrl } from "../urls";

interface CollectionCreateProps {
  coverageType: CoverageTypeEnum;
}

export const CollectionCreate: React.FC<CollectionCreateProps> = ({
  coverageType
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});
  const [thumbnailLinkImageUpload, setThumbnailLinkImage] = React.useState<
    File | undefined
  >();

  const [createCollection, createCollectionOpts] = useCollectionCreateMutation({
    onCompleted: data => {
      if (data?.collectionCreate?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        navigate(collectionUrl(data?.collectionCreate?.collection?.id || ""));
      } else if (
        data?.collectionCreate?.errors?.some(e => e.code === "INVALID") &&
        data?.collectionCreate?.errors?.some(e => e.field === "iconImage")
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "Header icon resolution should not exceed 500x500 pixels"
          })
        });
      } else {
        notify({
          status: "error",
          text: data?.collectionCreate?.errors[0].message
        });
      }
    }
  });

  const getPublicationData = ({
    publicationDate,
    isPublished
  }: CollectionCreatePageFormData) => ({
    isPublished: !!publicationDate || isPublished,
    publicationDate: publicationDate || null
  });

  const [iconToUpload, setIconToUpload] = React.useState<File | undefined>();

  const handleCreate = async (formData: CollectionCreatePageFormData) => {
    const result = await createCollection({
      variables: {
        input: {
          backgroundImage: formData.backgroundImage.value,
          backgroundImageAlt: formData.backgroundImageAlt,
          color: formData.color,
          coverageType,
          descriptionJson: JSON.stringify(formData.description),
          featuredIn: formData.featuredIn || [],
          iconImage: iconToUpload,
          name: formData.name,
          seo: {
            description: formData.seoDescription,
            title: formData.seoTitle
          },
          thumbnailLinkImage: thumbnailLinkImageUpload,
          ...getPublicationData(formData),
          isDirectPurchaseEnabled: formData.isDirectPurchaseEnabled
        }
      }
    });
    return result?.data?.collectionCreate?.collection?.id || "";
  };

  const handleSubmit = createMetadataCreateHandler(
    handleCreate,
    updateMetadata,
    updatePrivateMetadata
  );

  const label =
    coverageType === CoverageTypeEnum.NATIONWIDE ? "Nationwide" : "Local";

  return (
    <>
      <WindowTitle title={"Create " + label + " Collection"} />
      <CollectionCreatePage
        errors={createCollectionOpts?.data?.collectionCreate?.errors || []}
        onBack={() => navigate(collectionListUrl())}
        disabled={createCollectionOpts.loading}
        onSubmit={handleSubmit}
        onThumbnailLinkImageUpload={setThumbnailLinkImage}
        onIconToUpload={setIconToUpload}
        saveButtonBarState={createCollectionOpts.status}
        onFileSizeError={() =>
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage:
                "File size of the image you are trying to upload is too large."
            }),
            title: intl.formatMessage({
              defaultMessage: "Can't Upload."
            })
          })
        }
        coverageType={coverageType}
      />
    </>
  );
};
export default CollectionCreate;
